import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { flushSync } from "react-dom";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Model, Serializer } from "survey-core";
import { routes } from "../routes/routes";
import { useStore } from "../store/useStore";
import { useUserStore } from "../store/useUserStore";
import InstructionsModal from "../components/Modal/InstructionsModal";
import FinishAppModal from "../components/Modal/FinishAppModal";
import CloseApplicationModal from "../components/Modal/CloseApplicationModal";
import BreadcrumbsComponent from "../components/Breadcrumbs";
import AppQuestionnaireRightSide from "../components/AppQuestionnaireRightSide";
import AppQuestionnaireLeftSide from "../components/AppQuestionnaireLeftSide";
import useFetchCompleteParticipantApplication from "../hooks/useFetchCompleteParticipantApplication";
import useFetchAssignappQuestionnaire from "../hooks/useFetchAssignappQuestionnaire";
import useCreateSurveyResponse from "../hooks/useCreateSurveyResponse";
import useFetchInAppQuestionnaires from "../hooks/useFetchInAppQuestionnaires";
import useFetchSurveyResponse from "../hooks/useFetchSurveyResponse";
import useSendTimer from "../hooks/useSendTimer";
import useFetchTime from "../hooks/useFetchTime";
import WarningModal from "../components/Modal/WarningModal";
import ConfirmationModal from "../components/Modal/ConfirmationModal";
import useAssignappAdvancePhase from "../hooks/useAssignappAdvancePhase";
import useExpireInAppTime from "../hooks/useExpireInAppTime";
import useLookupOptionsSearch from "../hooks/useLookupOptionsSearch";
import useUpdateAssignappQuestionnaireStatus from "../hooks/useUpdateAssignappQuestionnaireStatus";

import {
  generateBreadcrumbs,
  surveyContentFormatter,
  generateRows,
  generateQuestionnaireContent,
  applyDefaultValuesToSurvey,
  generateAppStatus,
  handlePanelDynamicExtraControlsChange,
  handlePanelDynamicCountSelectChange,
  handlePanelDynamicLookupSelectChange,
} from "../utils/applicationPageLogic";
import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import { useMatrixSurveyTooltips } from "../hooks/useMatrixSurveyTooltips";

Serializer.addProperty("dropdown", {
  name: "x_lookup_source",
  category: "general",
});

Serializer.addProperty("dropdown", {
  name: "x_lookup_runtime:boolean",
  category: "general",
});

Serializer.addProperty("dropdown", {
  name: "x_lookup_min_query_length:number",
  category: "general",
});

Serializer.addProperty("dropdown", {
  name: "x_lookup_loaded:boolean",
  category: "general",
});

const Aplicacion = () => {
  const userInfo = useUserStore((state) => state.userInfo);
  const userID = userInfo?.id;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  const assignappQuestionnaireIdFromQuery = searchParams.get("cuestionarioAsignadoId");
  const hasProcessedAutoClose = useRef(false);
  const { searchLookupOptions } = useLookupOptionsSearch();
  const { updateStatus, error: withdrawError } = useUpdateAssignappQuestionnaireStatus();

  const {
    setIsModalInstructionsOpen,
    setQuestionnaires,
    assignappId,
    setAssignappId,
    isModalInstructionsOpen,
    resetStopwatchValue,
    setTimerVisible,
    showCloseApplicationModal,
    closeApplication,
    setCloseApplication,
    setUserHasClosedApplicationModal,
    applicationStartTime,
    setApplicationStartTime,
    surveyId,
    setSurveyId,
    userHasClosedApplicationModal,
    setShowCloseApplicationModal,
    applicacionData,
    setApplicacionData,
  } = useStore();

  // Estado para el modal de confirmación de retiro
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [pendingWithdrawId, setPendingWithdrawId] = useState(null);

  const { advancePhase, error: advancePhaseError } = useAssignappAdvancePhase();
  const isTimed = applicacionData?.timed ?? true;

  const { data: timeData, error: timeError } = useFetchTime(assignappId);
  const [openFinishAppModal, setOpenFinishAppModal] = useState(false);
  const [startApplication, setStartApplication] = useState(true);
  const [questionnaireStatus, setQuestionnaireStatus] = useState("pending");
  const [rows, setRows] = useState([]);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [isQuestionnaireComplete, setIsQuestionnaireComplete] = useState(false);
  const [openFinishQuestionnaireModal, setOpenFinishQuestionnaireModal] = useState(false);

  const { data, loading, error: applicationError, refetch } = useFetchCompleteParticipantApplication(id, userID);

  const { data: dataQuestionnaire, error: questionnaireFetchError } = useFetchAssignappQuestionnaire(assignappQuestionnaireIdFromQuery);

  const { createSurveyResponse, loading: updating, error: surveyResponseError } = useCreateSurveyResponse();

  const { createSurveyResponse: autoSaveSurveyResponse, error: autoSaveSurveyResponseError } = useCreateSurveyResponse();

  const { sendTimer, error: sendTimerError } = useSendTimer();

  const { expireInAppTime, error: expireInAppTimeError } = useExpireInAppTime();

  const surveyJson = dataQuestionnaire?.survey?.[0]?.content;
  //const documents = dataQuestionnaire?.documents;

  const [questionnaireError, setQuestionnaireError] = useState(null);
  const shouldCreateInitialResponse = useRef(true);
  const hasInitialDataLoaded = useRef(false);
  const didAutoRedirect = useRef(false);
  const currentAssignapp = data?.assignments?.[0] ?? null;
  const isAssignappLoaded = currentAssignapp !== null;
  const isReactivated = currentAssignapp?.reactivated === true;
  const [showAutoCloseWarning, setShowAutoCloseWarning] = useState(false);
  const [autoCloseAdvanceResult, setAutoCloseAdvanceResult] = useState(null);

  const timeAutoCloseMinutes = applicacionData?.time_auto_close_minutes ?? 165;
  const [timeLeftToClose, setTimeLeftToClose] = useState(timeAutoCloseMinutes);

  const [canRenderSurvey, setCanRenderSurvey] = useState(false);

  const resolvedSurveyId = useMemo(() => {
    if (!assignappQuestionnaireIdFromQuery) return null;

    const currentAssignappQuestionnaireId = Number(assignappQuestionnaireIdFromQuery);

    const fromAssignmentList = data?.assignment_questionnaires?.find(
      (item) => Number(item?.assignapp_questionnaire?.id) === currentAssignappQuestionnaireId,
    )?.survey_id;

    if (typeof fromAssignmentList === "string" && fromAssignmentList.trim()) {
      return fromAssignmentList;
    }

    return null;
  }, [assignappQuestionnaireIdFromQuery, data?.assignment_questionnaires]);

  const surveyModel = useMemo(() => {
    // No construye el modelo hasta haber verificado la respuesta
    if (!canRenderSurvey) return null;

    // Si no hay datos o no hay ID, no se construye el modelo.
    if (!surveyJson || !assignappQuestionnaireIdFromQuery) return null;
    try {
      // Verifica que el cuestionario tenga al menos una página con preguntas válidas.
      const hasQuestions =
        Array.isArray(surveyJson.pages) && surveyJson.pages.some((page) => Array.isArray(page.elements) && page.elements.length > 0);
      if (!hasQuestions) {
        return null;
      }

      const model = new Model(surveyJson);

      model.onOpenDropdownMenu.add((_, options) => {
        const isTouchDevice =
          window.matchMedia("(hover: none)").matches || window.matchMedia("(pointer: coarse)").matches || navigator.maxTouchPoints > 0;

        if (options.deviceType === "tablet") {
          // eslint-disable-next-line no-param-reassign
          options.menuType = isTouchDevice ? "overlay" : "dropdown";
        }
      });
      model.showNavigationButtons = false;
      model.storeOthersAsComment = false;
      model.width = "100%";
      model.showQuestionNumbers = "off";
      return model;
    } catch (error) {
      return null;
    }
  }, [canRenderSurvey, surveyJson, assignappQuestionnaireIdFromQuery]);

  useMatrixSurveyTooltips(surveyModel);

  // Handler que abre el modal
  const handleWithdrawClick = (assignappQuestionnaireId) => {
    setPendingWithdrawId(assignappQuestionnaireId);
    setShowWithdrawModal(true);
  };

  // Handler que confirma el retiro
  const handleConfirmWithdraw = async () => {
    if (!pendingWithdrawId) return;

    const response = await updateStatus(pendingWithdrawId, "withdrawn");

    if (response?.error) {
      setErrorMessage(response.error.message || "Error al retirar el cuestionario");
      setShowError(true);
    } else {
      setShowWithdrawModal(false);
      setPendingWithdrawId(null);
      refetch();
      refetchQuestionnaires();
    }
  };

  // Handler que cancela
  const handleCancelWithdraw = () => {
    setShowWithdrawModal(false);
    setPendingWithdrawId(null);
  };

  useEffect(() => {
    if (!surveyModel) return;

    const onChoicesLazyLoad = async (_, options) => {
      const question = options.question;

      const json = question?.jsonObj || {};
      const lazyEnabled = question?.choicesLazyLoadEnabled === true || json?.choicesLazyLoadEnabled === true;

      const lookupRuntime =
        json?.x_lookup_runtime === true ||
        json?.x_lookup_runtime === "true" ||
        question?.getPropertyValue?.("x_lookup_runtime") === true ||
        question?.getPropertyValue?.("x_lookup_runtime") === "true";

      const lookupSource = json?.x_lookup_source || question?.getPropertyValue?.("x_lookup_source");

      const minLength = Number(json?.x_lookup_min_query_length || question?.getPropertyValue?.("x_lookup_min_query_length") || 2);

      const filter = String(options.filter || "").trim();

      if (!lazyEnabled || !lookupRuntime || !lookupSource) {
        options.setItems([], 0);
        return;
      }

      if (filter.length < minLength) {
        // Si hay un valor ya seleccionado, devuelve ese choice para que
        // SurveyJS pueda mostrarlo correctamente sin hacer una búsqueda real
        const currentValue = options.question?.value;
        if (currentValue) {
          // Busca el texto en los choices que ya tiene la pregunta
          const existingChoice = options.question?.choices?.find((c) => c.value === currentValue);

          if (existingChoice) {
            options.setItems([{ value: existingChoice.value, text: existingChoice.text }], 1);
            return;
          }

          // Si no hay choice pre-cargado, busca el element_text en el panel padre
          const panel = options.question?.parentPanel;
          const panelValue = panel?.getValue?.() || {};
          const elementText = panelValue?.element_text;

          if (elementText) {
            options.setItems([{ value: currentValue, text: elementText }], 1);
            return;
          }

          // fallback que devuelve el ID como texto
          options.setItems([{ value: currentValue, text: currentValue }], 1);
          return;
        }

        options.setItems([], 0);
        return;
      }

      try {
        const response = await searchLookupOptions({
          lookupSource,
          query: filter,
          limit: options.take || 20,
        });

        const payload = response?.data || {};
        const items = Array.isArray(payload.options) ? payload.options : [];

        options.setItems(items, payload.totalCount ?? items.length);
      } catch (error) {
        console.error("[lookup error]", error);
        options.setItems([], 0);
      }
    };

    surveyModel.onChoicesLazyLoad.add(onChoicesLazyLoad);

    return () => {
      surveyModel.onChoicesLazyLoad.remove(onChoicesLazyLoad);
    };
  }, [surveyModel, searchLookupOptions]);

  useEffect(() => {
    if (!canRenderSurvey) return;
    if (!assignappQuestionnaireIdFromQuery) return;
    if (!surveyJson) return;

    const hasQuestions = Array.isArray(surveyJson.pages) && surveyJson.pages.some((page) => Array.isArray(page.elements) && page.elements.length > 0);

    if (!hasQuestions) {
      setQuestionnaireError("El cuestionario no contiene preguntas.");
    } else {
      setQuestionnaireError(null);
    }
  }, [canRenderSurvey, assignappQuestionnaireIdFromQuery, surveyJson]);

  const { fetchSurveyResponse, data: surveyResponseData, loading: responseLoading, error: fetchSurveyError } = useFetchSurveyResponse();

  const appName = data?.application?.name || "Cargando...";
  const maxDate = data?.application?.date_max;

  const stripHtml = (value) =>
    String(value || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();

  const hasApplicationInstructions = stripHtml(data?.application?.instructions || applicacionData?.instructions).length > 0;

  useEffect(() => {
    const currentAssignapp = data?.assignments?.[0];
    const currentApplication = data?.application;

    if (!currentAssignapp || !currentApplication) return;

    setAssignappId(currentAssignapp.id);

    setApplicacionData({
      name: currentApplication.name,
      instructions: currentApplication.instructions,
      timed: currentApplication.timed ?? true,
      time_warning_minutes: currentApplication.time_warning_minutes,
      time_close_warning_minutes: currentApplication.time_close_warning_minutes,
      time_auto_close_minutes: currentApplication.time_auto_close_minutes,
      reactivated: currentAssignapp.reactivated === true,
    });
  }, [data, setAssignappId, setApplicacionData]);

  const { data: questionnaires, error: questionnairesError, refetch: refetchQuestionnaires } = useFetchInAppQuestionnaires(assignappId);

  const totalQuestionnaires = questionnaires?.length || 0;
  const completedQuestionnaires = useMemo(() => questionnaires?.filter((s) => s.status === "finished")?.length || 0, [questionnaires]);
  const pendingQuestionnaires = useMemo(() => questionnaires?.filter((s) => s.status !== "finished")?.length || 0, [questionnaires]);

  const [showClosedByGestorModal, setShowClosedByGestorModal] = useState(false);

  const appState = useMemo(() => generateAppStatus(data?.assignments?.[0]?.status), [data?.assignments]);
  const disabledQuestionnairesButtons = appState === "Completado" ?? false;

  const progress = useMemo(() => {
    if (!questionnaires || questionnaires.length === 0) return 0;
    return (completedQuestionnaires / questionnaires.length) * 100;
  }, [questionnaires, completedQuestionnaires]);

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasFetchedSurveyResponse, setHasFetchedSurveyResponse] = useState(false);

  // Error al cargar el cuestionario o al cargar su respuesta guardada
  const hasLoadErrors = Boolean(questionnaireFetchError || questionnaireError || fetchSurveyError);

  const isLoadingResponse = Boolean(responseLoading && assignappQuestionnaireIdFromQuery);

  // Maneja el cierre de la modal cuando la aplicación fue cerrada por un gestor
  const handleRedirectModal = () => {
    didAutoRedirect.current = false;
    setTimerVisible(false);
    resetStopwatchValue();
    setUserHasClosedApplicationModal(false);
    setShowCloseApplicationModal(false);
    setShowClosedByGestorModal(false);

    navigate(routes.root, { replace: true });
  };

  const hasContextErrorsInSurvey = (surveyContent) => {
    const pages = surveyContent?.pages;
    if (!Array.isArray(pages)) return false;

    const walk = (els) => {
      if (!Array.isArray(els)) return false;

      for (const el of els) {
        const errs = el?.x_context_errors || el?.["x_context_errors"];
        if (Array.isArray(errs) && errs.length > 0) return true;

        if (walk(el?.elements)) return true;
        if (walk(el?.templateElements)) return true;
      }
      return false;
    };

    return pages.some((p) => walk(p?.elements));
  };

  const hasContextErrors = useMemo(() => {
    return hasContextErrorsInSurvey(surveyJson);
  }, [surveyJson]);

  // Indica que se ha cargado la información inicial
  useEffect(() => {
    if (!hasInitialDataLoaded.current && !loading && questionnaires) {
      hasInitialDataLoaded.current = true;
    }
  }, [loading, questionnaires]);

  // Indica que se ha cargado la respuesta del cuestionario
  useEffect(() => {
    if (!responseLoading && surveyResponseData) setHasFetchedSurveyResponse(true);
  }, [responseLoading, surveyResponseData]);

  // Se habilita cuando ya se verificó la respuesta
  useEffect(() => {
    const fetched = !responseLoading && hasFetchedSurveyResponse;
    setCanRenderSurvey(fetched);
  }, [responseLoading, hasFetchedSurveyResponse]);

  // Validaciones del cuestionario
  useEffect(() => {
    if (!assignappQuestionnaireIdFromQuery) {
      setQuestionnaireError(null);
      return;
    }

    // Mientras está cargando
    if (!dataQuestionnaire && !questionnaireFetchError) {
      setQuestionnaireError(null);
      return;
    }

    // Si falló el fetch del cuestionario
    if (questionnaireFetchError) {
      const backendMessage = questionnaireFetchError?.message || "Error al cargar la encuesta.";

      setQuestionnaireError(backendMessage);
      setErrorMessage(backendMessage);
      setShowError(true);

      navigate(`${routes.aplicacion}?id=${id}`, { replace: true });
      return;
    }

    // Si llegó data pero no hay contenido
    if (!dataQuestionnaire?.survey?.[0]?.content) {
      setQuestionnaireError("La encuesta no tiene contenido.");
      return;
    }

    setQuestionnaireError(null);
  }, [assignappQuestionnaireIdFromQuery, dataQuestionnaire, questionnaireFetchError, navigate, id]);

  // Restaura las respuestas anteriores si existen
  useEffect(() => {
    if (surveyModel && surveyResponseData?.content?.length && !responseLoading) {
      const updatedSurveyJson = applyDefaultValuesToSurvey(surveyJson, surveyResponseData.content);
      surveyModel.fromJSON(updatedSurveyJson);

      const restoredData = Object.fromEntries(
        surveyResponseData.content
          .filter((it) => it && Object.prototype.hasOwnProperty.call(it, "questionAnswer"))
          .map((it) => [it.questionId, it.questionAnswer]),
      );

      if (Object.keys(restoredData).length) {
        if (surveyModel.setValue) {
          Object.entries(restoredData).forEach(([name, value]) => {
            const q = surveyModel.getQuestionByName(name);
            if (q?.getType?.() === "paneldynamic") return;
            surveyModel.setValue(name, value);
          });
        } else if (surveyModel.mergeData) {
          surveyModel.mergeData(restoredData);
        } else {
          surveyModel.data = restoredData;
        }
      }

      setIsQuestionnaireComplete(isSurveyComplete(surveyModel));

      // Restaura la página donde quedó el usuario
      const savedPageName = surveyResponseData?.last_page_name;
      if (savedPageName) {
        const targetPage = surveyModel.getPageByName(savedPageName);
        // Solo navega si la página existe y es visible
        if (targetPage?.isVisible) {
          surveyModel.currentPage = targetPage;
        }
        // Si no es visible o no existe, SurveyJS queda en la primera página visible
      }
    }
  }, [surveyResponseData, responseLoading, surveyJson, surveyModel]);

  useEffect(() => {
    if (!assignappId) return;
    hasProcessedAutoClose.current = false;
    setUserHasClosedApplicationModal(false);
  }, [assignappId, setUserHasClosedApplicationModal]);

  const handleBreadcrumbQuestionnaire = () => {
    navigate(`${routes.aplicacion}?id=${id}`);
    refetch();
    refetchQuestionnaires();
  };

  //Comprueba si el cuestionario está completo
  const isSurveyComplete = (model) => {
    if (!model) return false;

    const questions = model.getAllQuestions(true);
    const requiredQuestions = [];
    //Valida las preguntas obligatorias, incluyendo las de paneles dinámicos
    questions.forEach((question) => {
      if (question.getType && question.getType() === "paneldynamic") {
        question.panels.forEach((panel) => {
          panel.questions.forEach((innerQ) => {
            if (innerQ.isVisible && innerQ.isRequired) {
              requiredQuestions.push(innerQ);
            }
          });
        });
      } else if (question.isRequired) {
        requiredQuestions.push(question);
      }
    });

    return requiredQuestions.every((question) => question.isAnswered);
  };

  const handleButtonClick = (cuestionarioAsignadoId, status, surveyIdMongo) => {
    flushSync(() => {
      setSurveyId(surveyIdMongo);
      setQuestionnaireStatus(status);
    });

    navigate(`${routes.aplicacion}?id=${id}&cuestionarioAsignadoId=${cuestionarioAsignadoId}`);
  };

  const breadcrumbs = generateBreadcrumbs(assignappQuestionnaireIdFromQuery, appName, handleBreadcrumbQuestionnaire);

  // Finaliza y guarda respuestas
  const handleFinishQuestionnaire = async () => {
    if (!resolvedSurveyId) return;
    const formattedContent = surveyContentFormatter(surveyModel.data, surveyModel);
    if (formattedContent.length === 0) return;

    const response = await createSurveyResponse({
      id: resolvedSurveyId,
      assignapp_id: assignappId,
      assignapp_questionnaire_id: Number(assignappQuestionnaireIdFromQuery),
      content: formattedContent,
      status: "finished",
    });

    if (response?.error) {
      setErrorMessage(response.error.message || "Error al terminar el cuestionario");
      setShowError(true);
      return;
    }

    navigate(`${routes.aplicacion}?id=${id}`);
    refetch();
    refetchQuestionnaires();
  };

  const handleRequestFinishQuestionnaire = useCallback(() => {
    setOpenFinishQuestionnaireModal(true);
  }, []);

  const handleCloseFinishQuestionnaireModal = () => {
    setOpenFinishQuestionnaireModal(false);
  };

  const handleConfirmFinishQuestionnaire = async () => {
    setOpenFinishQuestionnaireModal(false);
    await handleFinishQuestionnaire();
  };

  const handleSaveQuestionnaire = async () => {
    if (!surveyModel) return;
    if (!resolvedSurveyId) return;

    try {
      const content = surveyContentFormatter(surveyModel.data, surveyModel);
      const statusToSend = questionnaireStatus === "pending" ? "started" : "started";

      await createSurveyResponse({
        id: resolvedSurveyId,
        assignapp_id: assignappId,
        assignapp_questionnaire_id: Number(assignappQuestionnaireIdFromQuery),
        content,
        status: statusToSend,
        last_page_name: surveyModel.currentPage?.name ?? null,
      });

      navigate(`${routes.aplicacion}?id=${id}`);
      refetch();
      refetchQuestionnaires();
    } catch (error) {
      setErrorMessage(error?.message || "Error al guardar el cuestionario");
      setShowError(true);
    }
  };
  const disableFinish =
    updating ||
    questionnaireStatus === "finished" ||
    !isQuestionnaireComplete ||
    !dataQuestionnaire?.survey?.[0]?.content ||
    hasContextErrors ||
    hasLoadErrors ||
    isLoadingResponse;

  const questionnaireContent = useMemo(
    () =>
      generateQuestionnaireContent({
        questionnaire: canRenderSurvey ? surveyModel : null,
        questionnaireError: canRenderSurvey ? questionnaireError : "Verificando respuestas guardadas...",
        disabled: updating,
        onFinish: handleRequestFinishQuestionnaire,
        disableFinish,
      }),
    [canRenderSurvey, surveyModel, questionnaireError, updating, handleRequestFinishQuestionnaire, disableFinish],
  );

  const handleFinishApp = async () => {
    try {
      const response = await advancePhase(assignappId);
      const result = response?.data;

      if (!result) {
        throw new Error(response?.error?.message || "No fue posible avanzar la fase de la asignación");
      }

      if (isTimed) {
        const now = Date.now();
        const finalEpoc = Math.floor(now / 1000);
        await sendTimer({ endTime: finalEpoc, assignappId });
      }

      if (result.current_phase === "post") {
        navigate(`${routes.cuestionariosPorFase}?id=${id}&phase=post`, { replace: true });
      } else if (result.status === "complete") {
        navigate(routes.root, { replace: true });
      } else {
        navigate(`${routes.aplicacion}?id=${id}`, { replace: true });
      }
    } catch (error) {
      setErrorMessage(error?.message || "Error al terminar la fase de la aplicación");
      setShowError(true);
    } finally {
      setUserHasClosedApplicationModal(false);
      setShowCloseApplicationModal(false);
      setTimerVisible(false);
      resetStopwatchValue();
    }
  };

  // Maneja la confirmación del mensaje de cierre automático
  const handleAutoCloseConfirm = async () => {
    try {
      setShowAutoCloseWarning(false);
      setCloseApplication(false);
      setUserHasClosedApplicationModal(false);
      setShowCloseApplicationModal(false);
      setTimerVisible(false);
      resetStopwatchValue();
      let result = autoCloseAdvanceResult;
      if (!result) {
        const response = await advancePhase(assignappId);
        result = response?.data;

        if (!result) {
          throw new Error(response?.error?.message || "No fue posible obtener la fase actual de la asignación");
        }
      }

      if (result.status === "complete") {
        navigate(routes.root, { replace: true });
        return;
      }

      if (result.current_phase === "post") {
        navigate(`${routes.cuestionariosPorFase}?id=${id}&phase=post`, { replace: true });
        return;
      }

      navigate(routes.root, { replace: true });
    } catch (error) {
      setErrorMessage(error?.message || "Error al continuar luego de expirar el tiempo");
      setShowError(true);
    }
  };

  // Redirige según la fase real almacenada en el backend
  useEffect(() => {
    if (!isAssignappLoaded) return;

    const currentPhase = currentAssignapp?.current_phase;
    const assignmentStatus = currentAssignapp?.status;

    if (assignmentStatus === "complete") {
      navigate(routes.root, { replace: true });
      return;
    }

    if (currentPhase === "post") {
      navigate(`${routes.cuestionariosPorFase}?id=${id}&phase=post`, { replace: true });
    }
  }, [isAssignappLoaded, currentAssignapp?.current_phase, currentAssignapp?.status, navigate, id]);

  const handleInfoButton = () => {
    if (!hasApplicationInstructions) return;

    setStartApplication(false);
    setIsModalInstructionsOpen(true);
  };

  useEffect(() => {
    if (!assignappQuestionnaireIdFromQuery && hasInitialDataLoaded.current) {
      refetch();
      refetchQuestionnaires();
    }
  }, [assignappQuestionnaireIdFromQuery, refetch, refetchQuestionnaires]);

  // Actualiza el estado del cuestionario
  useEffect(() => {
    if (surveyResponseData?.status) setQuestionnaireStatus(surveyResponseData.status);
  }, [surveyResponseData]);

  // Obtiene respuesta asociada
  useEffect(() => {
    if (resolvedSurveyId && assignappId && assignappQuestionnaireIdFromQuery) {
      fetchSurveyResponse(resolvedSurveyId, assignappId, Number(assignappQuestionnaireIdFromQuery));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedSurveyId, assignappId, assignappQuestionnaireIdFromQuery]);

  useEffect(() => {
    setQuestionnaires({ finished: completedQuestionnaires, pending: pendingQuestionnaires, total: totalQuestionnaires });
  }, [completedQuestionnaires, pendingQuestionnaires, totalQuestionnaires, setQuestionnaires, questionnaires]);

  // Guarda automáticamente cada cambio de respuesta
  useEffect(() => {
    if (!surveyModel) return;
    if (questionnaireStatus === "finished") return;

    const handleAutoSave = async () => {
      const content = surveyContentFormatter(surveyModel.data, surveyModel);

      if (resolvedSurveyId && assignappId) {
        const statusToSend = questionnaireStatus === "pending" ? "started" : questionnaireStatus;

        const response = await autoSaveSurveyResponse({
          id: resolvedSurveyId,
          assignapp_id: assignappId,
          assignapp_questionnaire_id: Number(assignappQuestionnaireIdFromQuery),
          content,
          status: statusToSend,
          last_page_name: surveyModel.currentPage?.name ?? null,
        });

        if (response?.error) {
          setErrorMessage(response.error.message || "Error al guardar automáticamente");
          setShowError(true);
        } else {
          setShowError(false);
        }
      }
    };

    const onValueChanged = (sender, options) => {
      handlePanelDynamicExtraControlsChange({
        sender,
        options,
      });

      handlePanelDynamicCountSelectChange({
        sender,
        options,
      });

      handlePanelDynamicLookupSelectChange({
        sender,
        options,
      });

      handleAutoSave();
    };

    const onPageChanged = () => {
      handleAutoSave();
    };

    surveyModel.onValueChanged.add(onValueChanged);
    surveyModel.onCurrentPageChanged.add(onPageChanged);

    return () => {
      surveyModel.onValueChanged.remove(onValueChanged);
      surveyModel.onCurrentPageChanged.remove(onPageChanged);
    };
  }, [surveyModel, resolvedSurveyId, assignappId, questionnaireStatus, autoSaveSurveyResponse, assignappQuestionnaireIdFromQuery]);

  useEffect(() => {
    if (data) {
      const updatedRows = generateRows(data, handleButtonClick, disabledQuestionnairesButtons, handleWithdrawClick);
      setRows(updatedRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Detecta si la aplicación debe cerrarse automáticamente
  useEffect(() => {
    if (!isTimed) return;
    if (!assignappId) return;
    if (!isAssignappLoaded) return;
    if (isReactivated) return;

    if (currentAssignapp?.current_phase !== "in_app") return;

    const inAppTimeExpired = currentAssignapp?.in_app_time_expired === true;
    const isSystemInitiatedClose = closeApplication === true;

    if (!isSystemInitiatedClose && !inAppTimeExpired) return;
    if (hasProcessedAutoClose.current) return;

    const processAutoClose = async () => {
      hasProcessedAutoClose.current = true;
      didAutoRedirect.current = true;

      try {
        if (closeApplication) {
          setCloseApplication(false);
        }

        setTimerVisible(false);
        resetStopwatchValue();
        if (!inAppTimeExpired && isSystemInitiatedClose) {
          const expireResponse = await expireInAppTime(assignappId);

          if (expireResponse?.error) {
            throw new Error(expireResponse.error.message || "No fue posible registrar el vencimiento de la aplicación");
          }

          const now = Date.now();
          const finalEpoc = Math.floor(now / 1000);

          const timerResponse = await sendTimer({
            endTime: finalEpoc,
            assignappId,
          });

          if (timerResponse?.error) {
            throw new Error(timerResponse.error.message || "No fue posible guardar el tiempo final de la aplicación");
          }
        }
        const advanceResponse = await advancePhase(assignappId);
        const advanceResult = advanceResponse?.data;

        if (!advanceResult) {
          throw new Error(advanceResponse?.error?.message || "No fue posible avanzar la fase luego de vencer el tiempo");
        }

        setAutoCloseAdvanceResult(advanceResult);
        setShowAutoCloseWarning(true);
      } catch (error) {
        hasProcessedAutoClose.current = false;

        setErrorMessage(error?.message || "Ocurrió un error al procesar el vencimiento de la aplicación");
        setShowError(true);
      }
    };

    processAutoClose();
  }, [
    isTimed,
    assignappId,
    isAssignappLoaded,
    isReactivated,
    currentAssignapp?.current_phase,
    currentAssignapp?.in_app_time_expired,
    closeApplication,
    resetStopwatchValue,
    setTimerVisible,
    setCloseApplication,
    expireInAppTime,
    sendTimer,
    advancePhase,
  ]);

  useEffect(() => {
    const currentPhase = data?.assignments?.[0]?.current_phase;

    if (currentPhase !== "in_app") {
      setTimerVisible(false);
      return;
    }

    if (!isTimed) {
      setTimerVisible(true);
      return;
    }

    if (timeData?.start_time > 0) {
      setApplicationStartTime(timeData.start_time);
      setTimerVisible(true);
    }
  }, [data, isTimed, timeData, setApplicationStartTime, setTimerVisible]);

  useEffect(() => {
    const currentError =
      applicationError ||
      questionnaireFetchError ||
      questionnairesError ||
      fetchSurveyError ||
      sendTimerError ||
      expireInAppTimeError ||
      timeError ||
      advancePhaseError ||
      withdrawError;

    if (currentError) {
      setErrorMessage(currentError.message);
      setShowError(true);
    }
  }, [
    applicationError,
    questionnaireFetchError,
    questionnairesError,
    fetchSurveyError,
    sendTimerError,
    expireInAppTimeError,
    timeError,
    advancePhaseError,
    withdrawError,
  ]);

  useEffect(() => {
    if (!surveyModel) return;

    const checkCompletion = () => {
      setIsQuestionnaireComplete(isSurveyComplete(surveyModel));
    };

    checkCompletion();

    surveyModel.onValueChanged.add(checkCompletion);
    surveyModel.onCurrentPageChanged.add(checkCompletion);
    surveyModel.onQuestionVisibleChanged.add(checkCompletion);

    return () => {
      surveyModel.onValueChanged.remove(checkCompletion);
      surveyModel.onCurrentPageChanged.remove(checkCompletion);
      surveyModel.onQuestionVisibleChanged.remove(checkCompletion);
    };
  }, [surveyModel]);

  // Crea una respuesta inicial si es la primera vez que se accede
  useEffect(() => {
    const isQuestionnaireValid = surveyModel && resolvedSurveyId && assignappId && questionnaireStatus === "pending";
    const canCreateInitialResponse =
      isQuestionnaireValid && shouldCreateInitialResponse.current && hasFetchedSurveyResponse && !surveyResponseData?.status;

    if (canCreateInitialResponse) {
      shouldCreateInitialResponse.current = false;
      createSurveyResponse({
        id: resolvedSurveyId,
        assignapp_id: assignappId,
        assignapp_questionnaire_id: Number(assignappQuestionnaireIdFromQuery),
        content: [],
        status: "started",
      })
        .then(() => setQuestionnaireStatus("started"))
        .catch((error) => {
          setShowError(true);
          setErrorMessage(error.message);
        });
    }
  }, [
    surveyModel,
    resolvedSurveyId,
    assignappId,
    questionnaireStatus,
    createSurveyResponse,
    hasFetchedSurveyResponse,
    surveyResponseData,
    assignappQuestionnaireIdFromQuery,
  ]);

  // Indica que puede crearse una nueva respuesta inicial si cambia el cuestionario
  useEffect(() => {
    shouldCreateInitialResponse.current = true;
    setHasFetchedSurveyResponse(false);
  }, [assignappQuestionnaireIdFromQuery]);

  // Detecta si un gestor cerró manualmente la aplicación
  useEffect(() => {
    const assignmentStatus = data?.assignments?.[0]?.status;
    const isAppComplete = assignmentStatus === "complete";
    const gestorClosedManually = isAppComplete && !didAutoRedirect.current;
    if (gestorClosedManually) setShowClosedByGestorModal(true);
  }, [data]);

  useEffect(() => {
    if (!isTimed) return;
    if (!isAssignappLoaded) return;
    if (isReactivated) return;

    const shouldShowCloseModal = showCloseApplicationModal && !showCloseModal && !userHasClosedApplicationModal;

    if (shouldShowCloseModal) {
      setShowCloseModal(true);
    }
  }, [isTimed, isAssignappLoaded, isReactivated, showCloseApplicationModal, showCloseModal, userHasClosedApplicationModal]);

  // Calcula el tiempo restante para cierre automático
  useEffect(() => {
    if (!isTimed) return;
    if (!applicationStartTime || !timeAutoCloseMinutes) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const start = applicationStartTime * 1000;
      const elapsedMs = now - start;
      const elapsedMinutes = elapsedMs / (60 * 1000);
      const remaining = Math.max(0, Math.ceil(timeAutoCloseMinutes - elapsedMinutes));
      setTimeLeftToClose(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [isTimed, applicationStartTime, timeAutoCloseMinutes]);

  useEffect(() => {
    const isQuestionnaireView = Boolean(assignappQuestionnaireIdFromQuery);

    if (!isQuestionnaireView) return;
    if (responseLoading) return;
    if (!hasFetchedSurveyResponse) return;

    const isFinished = surveyResponseData?.status === "finished";

    if (isFinished) {
      setErrorMessage("Este cuestionario ya fue finalizado y no puede volver a abrirse.");
      setShowError(true);
      navigate(`${routes.aplicacion}?id=${id}`, { replace: true });
    }
  }, [assignappQuestionnaireIdFromQuery, responseLoading, hasFetchedSurveyResponse, surveyResponseData?.status, navigate, id]);

  const isQuestionnaireView = Boolean(assignappQuestionnaireIdFromQuery);

  const hasPostPhase = Boolean(data?.has_post_phase);

  const autoCloseModalDescription = useMemo(() => {
    if (hasPostPhase) {
      return "El tiempo para completar la fase de aplicación ha terminado. Al continuar, pasará a los cuestionarios posteriores.";
    }

    return "El tiempo para completar la fase de aplicación ha terminado. La asignación se dará por finalizada y será redirigido al inicio.";
  }, [hasPostPhase]);

  return (
    <>
      <div
        className={
          isQuestionnaireView
            ? "questionnaire-view mx-auto max-w-[1250px] px-3 sm:px-4 md:px-5 lg:px-6 py-4 sm:py-5 md:py-6"
            : "application-overview-page"
        }
      >
        <BreadcrumbsComponent breadcrumbs={breadcrumbs} />
        {isQuestionnaireView ? (
          <div className="mt-10 w-full">
            <AppQuestionnaireLeftSide
              data={data}
              assignappQuestionnaireId={assignappQuestionnaireIdFromQuery}
              hasApplicationInstructions={hasApplicationInstructions}
              updating={updating}
              dataQuestionnaire={dataQuestionnaire}
              handleInfoButton={handleInfoButton}
              handleFinishQuestionnaire={handleRequestFinishQuestionnaire}
              questionnaireContent={questionnaireContent}
              appState={appState}
              progress={progress}
              isQuestionnaireComplete={isQuestionnaireComplete}
              handleSaveQuestionnaire={handleSaveQuestionnaire}
              questionnaireStatus={questionnaireStatus}
              hasContextErrors={hasContextErrors}
              hasLoadErrors={hasLoadErrors}
              isLoadingResponse={isLoadingResponse}
            />
          </div>
        ) : (
          <div className="application-overview-content">
            <AppQuestionnaireLeftSide
              data={data}
              assignappQuestionnaireId={assignappQuestionnaireIdFromQuery}
              hasApplicationInstructions={hasApplicationInstructions}
              updating={updating}
              dataQuestionnaire={dataQuestionnaire}
              handleInfoButton={handleInfoButton}
              handleFinishQuestionnaire={handleRequestFinishQuestionnaire}
              questionnaireContent={questionnaireContent}
              appState={appState}
              progress={progress}
              isQuestionnaireComplete={isQuestionnaireComplete}
              hasLoadErrors={hasLoadErrors}
              isLoadingResponse={isLoadingResponse}
            />
            <AppQuestionnaireRightSide
              assignappQuestionnaireId={assignappQuestionnaireIdFromQuery}
              loading={loading}
              maxDate={maxDate}
              rows={rows}
              setOpenFinishAppModal={setOpenFinishAppModal}
            />
          </div>
        )}

        {hasApplicationInstructions && (
          <InstructionsModal
            openModal={isModalInstructionsOpen}
            data={data}
            setTimerVisible={setTimerVisible}
            setOpenModal={setIsModalInstructionsOpen}
            startApplication={startApplication}
            appState={appState}
          />
        )}

        <FinishAppModal
          openModal={openFinishAppModal}
          questionnaires={pendingQuestionnaires}
          setOpenModal={setOpenFinishAppModal}
          handleFinishApp={handleFinishApp}
        />

        <ConfirmationModal
          open={openFinishQuestionnaireModal}
          handleClose={handleCloseFinishQuestionnaireModal}
          handleConfirm={handleConfirmFinishQuestionnaire}
          title="¿Está seguro de que desea terminar el cuestionario?"
          description="Si termina el cuestionario, no podrá volver a abrirlo ni revisarlo. ¿Desea continuar?"
          confirmText="Sí, terminar"
          cancelText="Cancelar"
          confirmColor="error"
        />

        <ConfirmationModal
          open={showWithdrawModal}
          handleClose={handleCancelWithdraw}
          handleConfirm={handleConfirmWithdraw}
          title="¿Retiró este curso?"
          description={
            "Tenga en cuenta que esta información será verificada y, si resulta falsa, " +
            "la encuesta se considerará como no contestada y se aplicarán las medidas del caso. " +
            "¿Desea continuar?"
          }
          confirmText="Sí, retiré este curso"
          cancelText="Cancelar"
          confirmColor="error"
        />
      </div>

      <ErrorSnackbar open={showError} onClose={() => setShowError(false)} title="Error" message={errorMessage} />

      <CloseApplicationModal
        showCloseModal={showCloseModal}
        onClose={() => {
          setUserHasClosedApplicationModal(true);
          setShowCloseModal(false);
        }}
        minutesLeft={timeLeftToClose}
      />

      <WarningModal
        open={showClosedByGestorModal}
        onClose={handleRedirectModal}
        onConfirm={handleRedirectModal}
        title="Aplicación cerrada por un gestor"
        description="Un gestor ha cerrado esta aplicación. Será redirigido automáticamente al inicio."
      />

      <WarningModal
        open={showAutoCloseWarning}
        onClose={handleAutoCloseConfirm}
        onConfirm={handleAutoCloseConfirm}
        title="El tiempo se ha agotado"
        description={autoCloseModalDescription}
      />
    </>
  );
};

export default Aplicacion;
